import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, Activity, Settings, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/auth-context';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, logout } = useAuth();
  const displayUser = user || profile;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "fixed left-0 right-0 z-50 transition-all duration-500",
        scrolled || isOpen
          ? "glass-panel mx-auto max-w-[95%] top-4 shadow-2xl py-3"
          : "top-0 w-full bg-transparent border-b border-transparent py-7"
      )}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative h-10 w-10 overflow-hidden flex items-center justify-center bg-card rounded-xl shadow-lg border border-border">
              <picture className="flex items-center justify-center h-[70%] w-[70%]">
                <source srcSet="/logo.webp" type="image/webp" />
                <source srcSet="/logo.png" type="image/png" />
                <img src="/logo.jpg" alt="Fun Circle Games Creator Studio Logo" className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-500" />
              </picture>
            </div>
            <span className="font-display font-bold text-lg tracking-tighter uppercase group-hover:tracking-normal transition-all animate-gradient-text">
              Fun Circle Games
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <div className="flex items-center gap-6">
              <Link to="/" className={cn(
                "text-[10px] font-bold font-sans uppercase tracking-[0.2em] transition-colors",
                location.pathname === '/' ? 'text-primary' : 'text-muted-foreground hover:text-primary'
              )}>
                Home
              </Link>
              <button
                onClick={() => {
                  if (location.pathname !== '/') navigate('/');
                  setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
                className="text-[10px] font-bold font-sans uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
              >
                Pricing
              </button>


              <Link to="/studio" className={cn(
                "text-[10px] font-bold font-sans uppercase tracking-[0.2em] transition-colors px-3 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/20",
                location.pathname === '/studio' ? 'text-primary' : 'text-muted-foreground hover:text-primary'
              )}>
                Studio
              </Link>
            </div>

            <div className="h-4 w-px bg-border" />

            {displayUser ? (
              <div className="flex items-center gap-4">
                <Link to="/dashboard" className="button-ghost text-cyan-600 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-50">
                  Dashboard
                </Link>

                <div className="flex items-center gap-3">
                  <Link to="/settings" title="Account Settings" className="rounded-xl overflow-hidden active:scale-95 transition-transform shadow-sm border border-cyan-100">
                    {displayUser.picture ? (
                      <img src={displayUser.picture} alt={displayUser.username} className="w-10 h-10 object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-cyan-50 flex items-center justify-center">
                        <User className="w-5 h-5 text-cyan-600" />
                      </div>
                    )}
                  </Link>

                  <button
                    onClick={logout}
                    title="Logout"
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-red-500 hover:bg-red-50 transition-all border border-transparent hover:border-red-100"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" className="px-6 py-2.5 rounded-xl text-cyan-600 hover:text-cyan-700 text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-cyan-50/60 transition-colors border border-cyan-100/10 hover:border-cyan-100 flex items-center justify-center">Log In</Link>
                <Link to="/register" className={cn(buttonVariants(), "bg-cyan-600 text-white hover:bg-cyan-700 text-[10px] uppercase tracking-[0.3em] shadow-lg shadow-cyan-600/20 rounded-xl px-6")}>Sign Up</Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="text-cyan-950 hover:bg-cyan-50"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden"
          >
            <div className="px-8 py-6 space-y-6">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className="block text-xl font-bold font-display uppercase tracking-tighter text-cyan-950"
              >
                Home
              </Link>
              <button
                onClick={() => {
                  setIsOpen(false);
                  if (location.pathname !== '/') navigate('/');
                  setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
                className="block text-xl font-bold font-display uppercase tracking-tighter text-left text-zinc-400 hover:text-cyan-600"
              >
                Pricing
              </button>
              <Link
                to="/blog"
                onClick={() => setIsOpen(false)}
                className="block text-xl font-bold font-display uppercase tracking-tighter text-zinc-400 hover:text-cyan-600"
              >
                Blog
              </Link>
              <Link
                to="/how-it-works"
                onClick={() => setIsOpen(false)}
                className="block text-xl font-bold font-display uppercase tracking-tighter text-zinc-400 hover:text-cyan-600"
              >
                How it Works
              </Link>
              <Link
                to="/help"
                onClick={() => setIsOpen(false)}
                className="block text-xl font-bold font-display uppercase tracking-tighter text-zinc-400 hover:text-cyan-600"
              >
                Help & Guide
              </Link>
              <Link
                to="/studio"
                onClick={() => setIsOpen(false)}
                className="block text-xl font-bold font-display uppercase tracking-tighter text-primary animate-pulse"
              >
                Studio
              </Link>

              <div className="h-px bg-zinc-100 w-full" />

              {displayUser ? (
                <div className="space-y-4">
                  <Link to="/dashboard" onClick={() => setIsOpen(false)} className="block text-cyan-600 text-lg font-bold uppercase tracking-widest font-display">Dashboard</Link>
                  <Link to="/settings" onClick={() => setIsOpen(false)} className="block text-lg font-bold uppercase tracking-widest font-display text-zinc-500 hover:text-cyan-600">Settings</Link>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      logout();
                    }}
                    className="block text-red-500 text-lg font-bold uppercase tracking-widest font-display"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <Link to="/login" onClick={() => setIsOpen(false)} className={cn(buttonVariants({ variant: "ghost", size: "lg" }), "rounded-2xl h-14 uppercase font-bold font-sans tracking-[0.3em] text-xs w-full border border-cyan-100 bg-cyan-50/30 hover:bg-cyan-50 text-cyan-600")}>Log In</Link>
                  <Link to="/register" onClick={() => setIsOpen(false)} className={cn(buttonVariants({ size: "lg" }), "rounded-2xl h-14 uppercase font-bold font-sans tracking-[0.3em] text-xs w-full bg-cyan-600 text-white shadow-xl shadow-cyan-600/10 hover:bg-cyan-700")}>Sign Up</Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
